import { useCallback, useEffect, useState } from "react";
import { DatabaseBackup, Eye, KeyRound, ShieldCheck, Trash2, User, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { SECTIONS } from "../components/Layout";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { MaintenanceCard } from "../components/MaintenanceCard";
import { BgTasksCard } from "../components/BgTasksCard";
import { BranchTargetCard } from "../components/BranchTargetCard";
import { BranchDefaultProfilesCard } from "../components/BranchDefaultProfilesCard";
import { SisaVoucherSettingCard } from "../components/SisaVoucherSettingCard";
import { QuickRepliesSettingCard } from "../components/QuickRepliesSettingCard";
import { NhpLockSettingCard } from "../components/NhpLockSettingCard";
import { BankAccountsSettingCard } from "../components/BankAccountsSettingCard";

const ROLE_LABEL = { admin: "Super Admin", admin_fasnet: "Admin Fasnet", teknisi_kepala: "Teknisi Kepala", tuan_rumah: "Tuan Rumah", teknisi: "Teknisi", reseller: "Reseller" };

export default function Pengaturan() {
  const { user } = useAuth();
  const [form, setForm] = useState({ old_pin: "", new_pin: "", confirm_pin: "" });
  const [busy, setBusy] = useState(false);
  const [ttlBusy, setTtlBusy] = useState(false);
  const [ttlDone, setTtlDone] = useState(false);
  const [adminForm, setAdminForm] = useState({ name: "", email: "", pin: "" });
  const [adminBusy, setAdminBusy] = useState(false);
  const [admins, setAdmins] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [delBusy, setDelBusy] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPin, setResetPin] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const [menuHidden, setMenuHidden] = useState(null);
  const [menuBusy, setMenuBusy] = useState(false);

  const doResetPin = async () => {
    if (!resetTarget) return;
    setResetBusy(true);
    try {
      await api.post(`/users/${resetTarget.id}/reset-pin`, { pin: resetPin });
      toast.success(`PIN ${resetTarget.name} direset — PIN baru: ${resetPin}. Sesi aktifnya dikeluarkan, minta login ulang.`);
      setResetTarget(null);
      setResetPin("");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setResetBusy(false);
    }
  };

  const loadAdmins = useCallback(() => {
    if (user?.role !== "admin") return;
    api.get("/users").then((r) => setAdmins(r.data.filter((u) => ["admin", "admin_fasnet"].includes(u.role)))).catch(() => setAdmins([]));
  }, [user?.role]);

  useEffect(() => { loadAdmins(); }, [loadAdmins]);

  const doDeleteAdmin = async () => {
    if (!deleteTarget) return;
    setDelBusy(true);
    try {
      await api.delete(`/users/${deleteTarget.id}`);
      toast.success(`Admin ${deleteTarget.email} dihapus — jika login lagi, akunnya kembali berstatus menunggu aktivasi`);
      setDeleteTarget(null);
      loadAdmins();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setDelBusy(false);
    }
  };

  const submitAdmin = async (e) => {
    e.preventDefault();
    setAdminBusy(true);
    try {
      await api.post("/users", { name: adminForm.name.trim(), email: adminForm.email.trim(), pin: adminForm.pin, role: "admin_fasnet" });
      toast.success(`Admin ${adminForm.email.trim()} dibuat — minta login dengan Google lalu buka kunci pakai PIN awal ini`);
      setAdminForm({ name: "", email: "", pin: "" });
      loadAdmins();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setAdminBusy(false);
    }
  };

  const ensureTtl = async () => {
    setTtlBusy(true);
    try {
      await api.post("/admin/maintenance/ensure-backup-ttl");
      setTtlDone(true);
      toast.success("Retensi backup 14 hari aktif");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setTtlBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_pin !== form.confirm_pin) {
      toast.error("Konfirmasi PIN baru tidak sama");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-pin", { old_pin: form.old_pin, new_pin: form.new_pin });
      toast.success("PIN berhasil diubah");
      setForm({ old_pin: "", new_pin: "", confirm_pin: "" });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/config/hidden-menus").then((r) => setMenuHidden(r.data.hidden || [])).catch(() => setMenuHidden([]));
  }, [user?.role]);

  const saveMenuVisibility = async () => {
    setMenuBusy(true);
    try {
      const { data } = await api.put("/config/hidden-menus", { hidden: menuHidden });
      setMenuHidden(data.hidden);
      toast.success(`Visibilitas menu disimpan — ${data.hidden.length} menu disembunyikan`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setMenuBusy(false);
    }
  };

  const info = [
    { label: "Nama", value: user?.name || "-", testid: "settings-name" },
    { label: "Email", value: user?.email || "-", testid: "settings-email" },
    { label: "Role", value: ROLE_LABEL[user?.role] || user?.role || "-", testid: "settings-role" },
    ...(user?.agent_code ? [{ label: "Kode Agen", value: user.agent_code, testid: "settings-agent-code" }] : []),
  ];

  return (
    <div className="space-y-5 max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Pengaturan</h1>
        <p className="text-sm text-muted-foreground mt-1">Informasi akun dan keamanan PIN Anda.</p>
      </div>

      {user?.role === "admin" && <MaintenanceCard />}
      {user?.role === "admin" && <BgTasksCard />}
      <Card className="bg-card border-border shadow-sm" data-testid="settings-account-card">
        <CardHeader>
          <CardTitle className="text-base font-heading flex items-center gap-2"><User className="w-4 h-4 text-sky-500" /> Informasi Akun</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2.5">
          {info.map((it) => (
            <div key={it.label} className="flex items-center justify-between gap-3 text-sm border-b border-border/60 last:border-0 pb-2.5 last:pb-0">
              <span className="text-muted-foreground">{it.label}</span>
              <span className="font-mono font-semibold text-right break-all" data-testid={it.testid}>{it.value}</span>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="bg-card border-border shadow-sm" data-testid="settings-pin-card">
        <CardHeader>
          <CardTitle className="text-base font-heading flex items-center gap-2"><KeyRound className="w-4 h-4 text-[#F04E23]" /> Ubah PIN</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-3 max-w-xs">
            <div className="space-y-1.5">
              <Label>PIN Lama</Label>
              <Input type="password" inputMode="numeric" maxLength={8} value={form.old_pin} onChange={(e) => setForm({ ...form, old_pin: e.target.value })} placeholder="••••••" className="font-mono" data-testid="input-old-pin" />
            </div>
            <div className="space-y-1.5">
              <Label>PIN Baru (4-8 digit angka)</Label>
              <Input type="password" inputMode="numeric" maxLength={8} value={form.new_pin} onChange={(e) => setForm({ ...form, new_pin: e.target.value })} placeholder="••••••" className="font-mono" data-testid="input-new-pin" />
            </div>
            <div className="space-y-1.5">
              <Label>Konfirmasi PIN Baru</Label>
              <Input type="password" inputMode="numeric" maxLength={8} value={form.confirm_pin} onChange={(e) => setForm({ ...form, confirm_pin: e.target.value })} placeholder="••••••" className="font-mono" data-testid="input-confirm-pin" />
            </div>
            <Button type="submit" disabled={busy || form.old_pin.length < 4 || form.new_pin.length < 4} data-testid="btn-change-pin">
              <ShieldCheck className="w-4 h-4 mr-1.5" /> {busy ? "Menyimpan..." : "Simpan PIN Baru"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {user?.role === "admin" && (
        <Card className="bg-card border-border shadow-sm" data-testid="settings-add-admin-card">
          <CardHeader>
            <CardTitle className="text-base font-heading flex items-center gap-2"><UserPlus className="w-4 h-4 text-emerald-500" /> Tambah Admin</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground mb-3">Buat akun Admin Fasnet baru. Admin login lewat tombol "Masuk dengan Google" memakai email ini, lalu membuka kunci dengan PIN awal yang Anda buat.</p>
            <form onSubmit={submitAdmin} className="space-y-3 max-w-xs">
              <div className="space-y-1.5">
                <Label>Nama</Label>
                <Input required value={adminForm.name} onChange={(e) => setAdminForm({ ...adminForm, name: e.target.value })} placeholder="Nama admin" data-testid="input-admin-name" />
              </div>
              <div className="space-y-1.5">
                <Label>Email Google</Label>
                <Input required type="email" value={adminForm.email} onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })} placeholder="admin@gmail.com" data-testid="input-admin-email" />
              </div>
              <div className="space-y-1.5">
                <Label>PIN Awal (4-8 digit angka)</Label>
                <Input required type="password" inputMode="numeric" maxLength={8} value={adminForm.pin} onChange={(e) => setAdminForm({ ...adminForm, pin: e.target.value })} placeholder="••••••" className="font-mono" data-testid="input-admin-pin" />
              </div>
              <Button type="submit" disabled={adminBusy || adminForm.pin.length < 4 || !adminForm.name.trim() || !adminForm.email.includes("@")} data-testid="btn-add-admin">
                <UserPlus className="w-4 h-4 mr-1.5" /> {adminBusy ? "Membuat..." : "Buat Admin"}
              </Button>
            </form>

            <div className="mt-5 border-t border-border/60 pt-4" data-testid="admin-list">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Admin Terdaftar</p>
              {admins === null ? (
                <p className="text-xs text-muted-foreground">Memuat...</p>
              ) : admins.length === 0 ? (
                <p className="text-xs text-muted-foreground">Belum ada admin.</p>
              ) : (
                <div className="divide-y divide-border/60">
                  {admins.map((a) => (
                    <div key={a.id} className="flex items-center justify-between gap-3 py-2" data-testid={`admin-row-${a.id}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate">{a.name} {a.id === user?.id && <span className="text-[10px] font-bold text-sky-500">(Anda)</span>}</p>
                        <p className="text-xs text-muted-foreground truncate">{a.email} • {ROLE_LABEL[a.role] || a.role}</p>
                      </div>
                      {a.id !== user?.id && (
                        <div className="flex items-center gap-1.5 shrink-0">
                          <Button size="sm" variant="outline" className="text-amber-500 border-amber-500/40 hover:bg-amber-500/10" title="Reset PIN" onClick={() => { setResetTarget(a); setResetPin(""); }} data-testid={`btn-reset-pin-${a.id}`}>
                            <KeyRound className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" className="text-rose-500 border-rose-500/40 hover:bg-rose-500/10" title="Hapus admin" onClick={() => setDeleteTarget(a)} data-testid={`btn-delete-admin-${a.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {user?.role === "admin" && (
        <Card className="bg-card border-border shadow-sm" data-testid="settings-menu-visibility-card">
          <CardHeader>
            <CardTitle className="text-base font-heading flex items-center gap-2"><Eye className="w-4 h-4 text-emerald-500" /> Visibilitas Menu</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-muted-foreground">Hilangkan centang untuk menyembunyikan menu yang belum digunakan dari sidebar semua pengguna. Menu Dashboard & Pengaturan selalu tampil agar pengaturan ini tetap bisa diakses.</p>
            {menuHidden === null ? (
              <p className="text-xs text-muted-foreground">Memuat...</p>
            ) : (
              <>
                <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                  {SECTIONS.map((sec) => {
                    const items = sec.items.filter((it) => !["/", "/pengaturan"].includes(it.to));
                    if (!items.length) return null;
                    return (
                      <div key={sec.title}>
                        <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">{sec.title}</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                          {items.map((it) => (
                            <label key={it.to} className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-border/60 hover:bg-accent/50 cursor-pointer text-sm">
                              <input type="checkbox" className="accent-[#F04E23] w-4 h-4"
                                checked={!menuHidden.includes(it.to)}
                                onChange={() => setMenuHidden((h) => (h.includes(it.to) ? h.filter((p) => p !== it.to) : [...h, it.to]))}
                                data-testid={`menu-vis-${it.to.replace(/\//g, "-").slice(1)}`} />
                              <span className="truncate">{it.label}</span>
                              <span className="ml-auto text-[9px] font-mono text-muted-foreground">{it.to}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <Button onClick={saveMenuVisibility} disabled={menuBusy} data-testid="btn-save-menu-visibility">
                  <ShieldCheck className="w-4 h-4 mr-1.5" /> {menuBusy ? "Menyimpan..." : "Simpan Visibilitas Menu"}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {user?.role === "admin" && <NhpLockSettingCard />}
      {user?.role === "admin" && <SisaVoucherSettingCard />}
      {user?.role === "admin" && user?.is_owner && <BankAccountsSettingCard />}
      {user?.role === "admin" && <QuickRepliesSettingCard />}
      {user?.role === "admin" && <BranchTargetCard />}
      {user?.role === "admin" && <BranchDefaultProfilesCard />}

      {user?.role === "admin" && (
        <Card className="bg-card border-border shadow-sm" data-testid="settings-maintenance-card">
          <CardHeader>
            <CardTitle className="text-base font-heading flex items-center gap-2"><DatabaseBackup className="w-4 h-4 text-sky-500" /> Pemeliharaan Database</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-xs text-muted-foreground">Mengaktifkan penghapusan otomatis data backup router yang berusia lebih dari 14 hari (retensi backup). Cukup klik sekali — aman jika diulang.</p>
            <Button variant="outline" onClick={ensureTtl} disabled={ttlBusy || ttlDone} data-testid="btn-ensure-backup-ttl">
              <DatabaseBackup className="w-4 h-4 mr-1.5" /> {ttlDone ? "Retensi Backup Aktif" : ttlBusy ? "Memproses..." : "Aktifkan Retensi Backup 14 Hari"}
            </Button>
          </CardContent>
        </Card>
      )}

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="dialog-confirm-delete-admin">
          <DialogHeader>
            <DialogTitle className="font-heading">Hapus Admin?</DialogTitle>
            <DialogDescription>
              {deleteTarget?.name} ({deleteTarget?.email}) akan kehilangan akses admin. Jika login Google lagi, akunnya kembali berstatus menunggu aktivasi.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} data-testid="btn-cancel-delete-admin">Batal</Button>
            <Button className="bg-rose-600 hover:bg-rose-500 text-white" onClick={doDeleteAdmin} disabled={delBusy} data-testid="btn-confirm-delete-admin">
              {delBusy ? "Menghapus..." : "Ya, Hapus"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetTarget} onOpenChange={(o) => { if (!o) { setResetTarget(null); setResetPin(""); } }}>
        <DialogContent className="max-w-sm" data-testid="dialog-reset-pin">
          <DialogHeader>
            <DialogTitle className="font-heading">Reset PIN Admin</DialogTitle>
            <DialogDescription>
              Buat PIN baru untuk {resetTarget?.name} ({resetTarget?.email}). Semua sesi aktifnya akan dikeluarkan — minta dia login Google lagi lalu buka kunci dengan PIN baru.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); doResetPin(); }} className="space-y-3">
            <div className="space-y-1.5">
              <Label>PIN Baru (4-8 digit angka)</Label>
              <Input required type="password" inputMode="numeric" maxLength={8} value={resetPin} onChange={(e) => setResetPin(e.target.value)} placeholder="••••••" className="font-mono" data-testid="input-reset-pin" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button type="button" variant="outline" onClick={() => { setResetTarget(null); setResetPin(""); }} data-testid="btn-cancel-reset-pin">Batal</Button>
              <Button type="submit" disabled={resetBusy || resetPin.length < 4 || !/^\d+$/.test(resetPin)} data-testid="btn-confirm-reset-pin">
                {resetBusy ? "Mereset..." : "Reset PIN"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
